import { useEffect, useState } from 'react'
import { AlertCircle, DollarSign } from 'lucide-react'
import { useGetProfitability } from '../../hooks/backend/dashboardPage'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../lib/shadcn/card'
import { Badge } from '../../lib/shadcn/badge'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Skeleton } from '../../lib/shadcn/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../lib/shadcn/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../lib/shadcn/table'
import { cn } from '../../lib/shadcn/utils'
import { HelpText } from '../../components/HelpText'

interface CustomerProfit {
  customer_id: string | null
  customer: string
  orders: number
  revenue: number
  shipping_cost: number
  costed_share: number
  product_margin: number | null
  profit: number | null
  estimated_profit: number | null
  margin: number | null
}

interface FlavorProfit {
  flavor: string
  bags: number
  revenue: number
  cost: number | null
  profit: number | null
  margin: number | null
  cost_per_bag: number | null
  revenue_per_bag: number
}

interface Profitability {
  days: number
  summary: {
    orders: number
    skippedOrders: number
    revenue: number
    shipping_cost: number
    costed_share: number
    product_margin: number | null
  }
  customers: CustomerProfit[]
  flavors: FlavorProfit[]
}

const RANGES = [
  { days: 90, label: 'Last 90 days' },
  { days: 365, label: 'Last 12 months' },
  { days: 0, label: 'All orders' },
]

const money = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })

const money2 = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD' })

const pct = (n: number) => `${Math.round(n * 100)}%`

const dash = <span className="text-muted-foreground">—</span>

function MarginCell({ value }: { value: number | null }) {
  if (value == null) return dash
  return (
    <span
      className={cn(
        'tabular-nums',
        value >= 0 ? 'text-emerald-600 dark:text-emerald-500' : 'text-destructive',
      )}
    >
      {pct(value)}
    </span>
  )
}

export default function ProfitabilityPanel() {
  const getProfitability = useGetProfitability()
  const [days, setDays] = useState(90)

  useEffect(() => {
    getProfitability.trigger({ days })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days])

  const data = getProfitability.data as Profitability | null | undefined
  const loading = getProfitability.loading && !data
  const summary = data?.summary
  const customers = data?.customers ?? []
  const flavors = data?.flavors ?? []
  const uncostedFlavors = flavors.filter(f => f.cost == null).length

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-4">
        <div className="space-y-1.5">
          <CardTitle className="text-base flex items-center gap-2">
            <DollarSign className="w-4 h-4" />
            Profit by customer and flavor
          </CardTitle>
          <CardDescription><HelpText>
            Revenue minus recipe and fixed costs (bags, labels) and shipping labels. Flavors
            without a complete recipe have no cost, so profit for customers who buy them is
            estimated from the flavors that do.
          </HelpText></CardDescription>
        </div>
        <NativeSelect
          value={String(days)}
          onChange={e => setDays(Number(e.target.value))}
          className="h-9 w-40 shrink-0"
          aria-label="Date range"
        >
          {RANGES.map(r => (
            <option key={r.days} value={r.days}>
              {r.label}
            </option>
          ))}
        </NativeSelect>
      </CardHeader>
      <CardContent className="space-y-4">
        {getProfitability.error && (
          <div className="flex items-center gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {getProfitability.error}
          </div>
        )}

        {loading || !summary ? (
          <div className="space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="rounded-md border p-3">
                <div className="text-xs text-muted-foreground">Revenue</div>
                <div className="text-lg font-semibold tabular-nums">{money(summary.revenue)}</div>
                <div className="text-xs text-muted-foreground">{summary.orders} orders</div>
              </div>
              <div className="rounded-md border p-3">
                <div className="text-xs text-muted-foreground">Product margin</div>
                <div className="text-lg font-semibold">
                  <MarginCell value={summary.product_margin} />
                </div>
                <div className="text-xs text-muted-foreground">on costed flavors</div>
              </div>
              <div className="rounded-md border p-3">
                <div className="text-xs text-muted-foreground">Shipping labels</div>
                <div className="text-lg font-semibold tabular-nums">{money(summary.shipping_cost)}</div>
                <div className="text-xs text-muted-foreground">
                  {summary.revenue > 0 ? `${pct(summary.shipping_cost / summary.revenue)} of revenue` : ''}
                </div>
              </div>
              <div className="rounded-md border p-3">
                <div className="text-xs text-muted-foreground">Revenue with known cost</div>
                <div className="text-lg font-semibold tabular-nums">{pct(summary.costed_share)}</div>
                <div className="text-xs text-muted-foreground">
                  {uncostedFlavors > 0
                    ? `${uncostedFlavors} flavor${uncostedFlavors === 1 ? '' : 's'} need a recipe`
                    : 'every flavor costed'}
                </div>
              </div>
            </div>

            {summary.skippedOrders > 0 && (
              <p className="text-xs text-muted-foreground">
                {summary.skippedOrders} cleared order{summary.skippedOrders === 1 ? '' : 's'} in this
                range {summary.skippedOrders === 1 ? 'has' : 'have'} no line items left, so{' '}
                {summary.skippedOrders === 1 ? "it's" : "they're"} not included.
              </p>
            )}

            <Tabs defaultValue="customers" className="space-y-3">
              <TabsList>
                <TabsTrigger value="customers">Customers</TabsTrigger>
                <TabsTrigger value="flavors">Flavors</TabsTrigger>
              </TabsList>

              <TabsContent value="customers">
                {customers.length === 0 ? (
                  <p className="py-6 text-sm text-muted-foreground text-center">No orders in this range.</p>
                ) : (
                  <div className="rounded-md border overflow-auto max-h-[480px]">
                    <Table>
                      <TableHeader className="sticky top-0 z-10 bg-card">
                        <TableRow>
                          <TableHead>Customer</TableHead>
                          <TableHead className="text-right">Orders</TableHead>
                          <TableHead className="text-right">Revenue</TableHead>
                          <TableHead className="text-right">Shipping</TableHead>
                          <TableHead className="text-right">Product margin</TableHead>
                          <TableHead className="text-right">Profit</TableHead>
                          <TableHead className="text-right">Margin</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {customers.map(c => {
                          const value = c.profit ?? c.estimated_profit
                          return (
                            <TableRow key={c.customer_id ?? c.customer}>
                              <TableCell className="font-medium">{c.customer}</TableCell>
                              <TableCell className="text-right tabular-nums">{c.orders}</TableCell>
                              <TableCell className="text-right tabular-nums">{money(c.revenue)}</TableCell>
                              <TableCell className="text-right tabular-nums">
                                {c.shipping_cost > 0 ? money(c.shipping_cost) : dash}
                              </TableCell>
                              <TableCell className="text-right">
                                <MarginCell value={c.product_margin} />
                              </TableCell>
                              <TableCell className="text-right">
                                {value == null ? (
                                  <Badge variant="outline">No costed flavors</Badge>
                                ) : (
                                  <div title={c.profit == null ? `Estimated: ${pct(c.costed_share)} of this revenue has a known cost` : undefined}>
                                    <div className="tabular-nums font-medium">{money(value)}</div>
                                    {c.profit == null && (
                                      <div className="text-xs text-muted-foreground">
                                        est. · {pct(c.costed_share)} costed
                                      </div>
                                    )}
                                  </div>
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                <MarginCell value={c.margin} />
                              </TableCell>
                            </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="flavors">
                {flavors.length === 0 ? (
                  <p className="py-6 text-sm text-muted-foreground text-center">No orders in this range.</p>
                ) : (
                  <div className="rounded-md border overflow-auto max-h-[480px]">
                    <Table>
                      <TableHeader className="sticky top-0 z-10 bg-card">
                        <TableRow>
                          <TableHead>Flavor</TableHead>
                          <TableHead className="text-right">Bags</TableHead>
                          <TableHead className="text-right">Revenue</TableHead>
                          <TableHead className="text-right">Per bag</TableHead>
                          <TableHead className="text-right">Cost per bag</TableHead>
                          <TableHead className="text-right">Profit</TableHead>
                          <TableHead className="text-right">Margin</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {flavors.map(f => (
                          <TableRow key={f.flavor}>
                            <TableCell className="font-medium">{f.flavor}</TableCell>
                            <TableCell className="text-right tabular-nums">{f.bags.toLocaleString('en-US')}</TableCell>
                            <TableCell className="text-right tabular-nums">{money(f.revenue)}</TableCell>
                            <TableCell className="text-right tabular-nums">{money2(f.revenue_per_bag)}</TableCell>
                            <TableCell className="text-right tabular-nums">
                              {f.cost_per_bag != null ? money2(f.cost_per_bag) : <Badge variant="outline">No cost</Badge>}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              {f.profit != null ? money(f.profit) : dash}
                            </TableCell>
                            <TableCell className="text-right">
                              <MarginCell value={f.margin} />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </>
        )}
      </CardContent>
    </Card>
  )
}
